import React, { useEffect, useState } from "react";
import hide from "./../../assets/Images/hide.svg";
import noHide from "./../../assets/Images/noHide.svg";
import userProfileImage from "./../../assets/Images/transparent-user-profile.png";
import usePatchData from "./../Custom-Hooks/usePatchData";
import useGetData from "../Custom-Hooks/useGetData";
import FancyTree from "../shared/FancyTree";
import GlobalLoader from "../shared/GlobalLoader";
import { useLoading } from "../../context/LoadingContext";
import SuccessPopup from "../shared/SuccessPopup";
import axios from "axios";
import { truncateText } from "../../utils/textTruncateUtils";
import { useNavigate } from "react-router-dom";
import { downloadWopiFile } from "../../services/wopi.service";
import { toast } from "react-toastify";
import { useAppContext } from "../../context/AppContext.tsx";

const UserProfile = () => {
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const accessToken = sessionStorage.getItem("accessToken");
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const [activeTab, setActiveTab] = useState("overview");
  const [isResetting, setIsResetting] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState({ newPassword: "", confirmPassword: "" });
  const [message, setMessage] = useState("");
  const passwordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
  const [errorMsg, setErrorMsg] = useState("");
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [pageAccessData, setPageAccessData] = useState([]);
  const [selectedPageAccess, setSelectedPageAccess] = useState([]);
  const [selectedTreeAccess, setSelectedTreeAccess] = useState([]);
  const [treeData, setTreeData] = useState([]);
  const { isLoading, setLoading } = useLoading();
  const [shouldRefetch, setShouldRefetch] = useState(false);
  const [adminCustomers, setAdminCustomers] = useState([]);
  const [customer, setCustomer] = useState("");
  const [locationSystems, setLocationSystems] = useState({});
  const [showSuccessPopup, setShowSuccessPopup] = useState({
    type: "",
    show: false,
    message: "",
  });
  const [shouldRedirectAfterPopup, setShouldRedirectAfterPopup] =
    useState(false);

  const initialFormData = {
    firstname: "",
    lastname: "",
    username: "",
    email: "",
    role_name: "",
  };
  const [formData, setFormData] = useState(initialFormData);
  const isAdmin = userDetails?.id === 1;

  // get all users data from API
  const {
    data: allUserData,
    isgetLoading: getLoading,
    refetch,
  } = useGetData(`${baseUrl}/user/${userDetails?.id}`);
  const { user, setUser } = useAppContext();

  const headers = accessToken
    ? {
        Authorization: `${accessToken}`,
      }
    : {};

  useEffect(() => {
    const fetchAdminCustomers = async () => {
      try {
        const userId = parseInt(userDetails?.id);
        const response = await axios.post(
          `${baseUrl}/customer`,
          { userId },
          { headers }
        );

        const customerList = (response?.data || []).map((c) => ({
          id: c.id,
          name: c.name,
        }));
        setAdminCustomers(customerList);

        if (customerList.length > 0) {
          const storedCustomerId = localStorage.getItem("customerId");
          if (storedCustomerId) {
            const storedCustomer = customerList.find(
              (c) => c.id === parseInt(storedCustomerId)
            );
            setCustomer(
              storedCustomer ? storedCustomer.name : customerList[0].name
            );
          } else {
            setCustomer(customerList[0].name);
          }
        }
      } catch (error) {
        console.error("Error fetching admin customer data:", error);
      }
    };

    fetchAdminCustomers();
  }, []);

  useEffect(() => {
    const fetchLocationSystems = async () => {
      try {
        const systemsData = {};

        await Promise.all(
          adminCustomers.map(async (customer) => {
            const res = await axios.get(
              `${baseUrl}/customer/${customer.id}/locationSystems`,
              { headers }
            );
            systemsData[customer.id] = res.data;
          })
        );

        setLocationSystems(systemsData);
      } catch (error) {
        console.error("Error fetching location systems:", error);
      }
    };

    if (
      allUserData?.data?.role_name?.toLowerCase() !== "admin" &&
      adminCustomers.length > 0
    ) {
      fetchLocationSystems();
    }
  }, [allUserData, adminCustomers]);

  // Build tree data when both adminCustomers and locationSystems are ready
  useEffect(() => {
    if (adminCustomers.length > 0 && Object.keys(locationSystems).length > 0) {
      const preparedTree = prepareTreeData(adminCustomers, locationSystems);
      setTreeData(preparedTree);
    }
  }, [adminCustomers, locationSystems]);

  useEffect(() => {
    const fetchAvatar = async () => {
      if (allUserData?.data?.avatar) {
        try {
          const response = await downloadWopiFile(allUserData?.data?.avatar, {
            responseType: "blob",
          });
          setAvatarPreview(URL.createObjectURL(response));
        } catch (err) {
          console.error("Error downloading avatar file:", err);
        }
      }
    };

    fetchAvatar();
  }, [allUserData]);

  // Prepare tree structure
  // Prepare tree structure with encoded keys
  const prepareTreeData = (adminCustomers, locationSystems) => {
    return adminCustomers.map((customer) => {
      const customerId = customer.id;
      const customerNode = {
        id: customerId,
        name: customer.name,
        key: `cust-${customerId}`,
        value: `cust-${customerId}`,
        children: [],
      };

      const locations = locationSystems[customerId] || [];
      customerNode.children = locations.map((location) => {
        const locationId = location.id;
        const locationNode = {
          id: locationId,
          name: location.plant_name,
          key: `loc-${locationId}`,
          value: `loc-${locationId}`,
          children: (location.systems || []).map((system) => {
            const systemId = system.id;
            return {
              id: systemId,
              name: system.system_name,
              key: `sys-${systemId}`,
              value: `sys-${systemId}`,
            };
          }),
        };
        return locationNode;
      });

      return customerNode;
    });
  };

  useEffect(() => {
    if (shouldRefetch) {
      refetch();
      setShouldRefetch(false);
    }
  }, [shouldRefetch, refetch]);

  useEffect(() => {
    if (allUserData?.data) {
      const user = allUserData.data;
      setFormData({
        firstname: user?.firstname || "",
        lastname: user?.lastname || "",
        username: user?.username || "",
        email: user?.email || "",
        role_name: user?.role_name || "",
      });
      setPageAccessData(user);
    }
  }, [allUserData]);

  const filterAccessTree = (nodes, permissions) => {
    return nodes
      .map((node) => {
        const hasPermission = permissions.includes(node.id);
        const children = node.children
          ? filterAccessTree(node.children, permissions)
          : [];

        if (hasPermission || children.length > 0) {
          return {
            ...node,
            children: children.length > 0 ? children : undefined,
          };
        }

        return null;
      })
      .filter(Boolean); // remove nulls
  };

  const filterPlantTree = (nodes, permissions) => {
    return nodes
      .map((node) => {
        const hasPermission = permissions.includes(node.id);
        const children = node.children
          ? filterPlantTree(node.children, permissions)
          : [];

        if (hasPermission || children.length > 0) {
          return {
            ...node,
            children: children.length > 0 ? children : undefined,
          };
        }

        return null;
      })
      .filter(Boolean); // remove nulls
  };
  const accessControl = JSON.parse(allUserData?.data?.access_control || "[]");

  const featurePermissions = allUserData?.data?.featurePermissions || [];
  const filteredTree = filterAccessTree(accessControl, featurePermissions);

  const customerPermissions = (
    allUserData?.data?.customerLocationSystemPermissions || []
  )
    .filter((p) => p.entityType === "CUSTOMER")
    .map((p) => p.entityId);

  const locationPermissions = (
    allUserData?.data?.customerLocationSystemPermissions || []
  )
    .filter((p) => p.entityType === "LOCATION")
    .map((p) => p.entityId);

  const systemPermissions = (
    allUserData?.data?.customerLocationSystemPermissions || []
  )
    .filter((p) => p.entityType === "SYSTEM")
    .map((p) => p.entityId);

  const plantTreePermissions = [
    ...customerPermissions,
    ...locationPermissions,
    ...systemPermissions,
  ];

  const encodeKey = (id, type) => {
    if (type === "CUSTOMER") return `cust-${id}`;
    if (type === "LOCATION") return `loc-${id}`;
    if (type === "SYSTEM") return `sys-${id}`;
    return id;
  };

  const initialPlantKeys =
    allUserData?.data?.customerLocationSystemPermissions?.map((p) =>
      encodeKey(p.entityId, p.entityType)
    ) || [];

  const filteredPlantTree = filterPlantTree(treeData, plantTreePermissions);

  const {
    patch,
    isLoading: patchLoading,
    patchError,
  } = usePatchData(`${baseUrl}/auth/reset-password`);

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
      if (!allowedTypes.includes(file.type)) {
        setErrorMsg("Only PNG, JPG, and JPEG formats are allowed.");
        e.target.value = ""; // Reset the input
        return;
      }

      setErrorMsg(""); // Clear previous error
      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
    }
  };

  const handleDiscardUserDetails = () => {
    const user = allUserData.data;
    setFormData({
      firstname: user?.firstname || "",
      lastname: user?.lastname || "",
      username: user?.username || "",
      email: user?.email || "",
      role_name: user?.role_name || "",
    });
  };

  const handleEditUser = async () => {
    const fd = new FormData();
    fd.append("firstname", formData.firstname);
    fd.append("lastname", formData.lastname);
    if (avatarFile) fd.append("avatar", avatarFile);
    try {
      setLoading(true);
      const response = await axios.put(
        `${baseUrl}/user/${pageAccessData?.id}`,
        fd,
        { headers }
      );

      if (response?.status === 200) {
        toast.success(`User Updated Successfully`, {
          autoClose: 3000,
          closeButton: true,
          theme: "colored",
        });

        const updatedUser = {
          ...user,
          firstname: formData.firstname,
          lastname: formData.lastname,
          avatar: avatarFile,
        };
        setUser(updatedUser);
      }
      setShouldRefetch(true);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.error(`Failed to update user details`, {
        autoClose: 3000,
        closeButton: true,
        theme: "colored",
      });
    }
  };

  // Validate new password while typing
  const handleNewPasswordChange = (e) => {
    const value = e.target.value;
    setNewPassword(value);

    if (!passwordRegex.test(value)) {
      setError((prev) => ({
        ...prev,
        newPassword:
          "Minimum 8 characters, including 1 uppercase, 1 number, and 1 special character.",
      }));
    } else {
      setError((prev) => ({ ...prev, newPassword: "" }));
    }

    // Reset confirm password error when user types in new password
    if (confirmPassword && confirmPassword !== value) {
      setError((prev) => ({
        ...prev,
        confirmPassword: "Passwords do not match !!!",
      }));
    } else {
      setError((prev) => ({ ...prev, confirmPassword: "" }));
    }
  };

  // Validate confirm password while typing
  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmPassword(value);

    if (value !== newPassword) {
      setError((prev) => ({
        ...prev,
        confirmPassword: "Passwords do not match!",
      }));
    } else {
      setError((prev) => ({ ...prev, confirmPassword: "" }));
    }
  };

  const handleResetPassword = () => {
    setIsResetting(true);
    setNewPassword("");
    setConfirmPassword("");
    setError({ newPassword: "", confirmPassword: "" });
    setMessage("");
  };

  const handleUpdatePassword = async () => {
    if (!passwordRegex.test(newPassword) || newPassword !== confirmPassword) {
      return;
    }

    try {
      const res = await patch({
        currentPassword,
        newPassword,
      });

      if (res?.status === 200) {
        setShowSuccessPopup({
          type: "success",
          show: true,
          message: "Password Updated Successfully",
        });
        setIsResetting(false);
        setNewPassword("");
        setConfirmPassword("");
        setError({ newPassword: "", confirmPassword: "" });
        setShouldRedirectAfterPopup(true);
      } else {
        setShowSuccessPopup({
          type: "warning",
          show: true,
          message: "Failed to update password. Try again.",
        });
        setShouldRedirectAfterPopup(false);
      }
    } catch (error) {
      setShowSuccessPopup({
        type: "warning",
        show: true,
        message: "Failed to update password. Try again.",
      });
    }
  };

  // **Disable button** if there are errors or fields are empty
  const isButtonDisabled =
    !currentPassword ||
    !newPassword ||
    !confirmPassword ||
    error.newPassword ||
    error.confirmPassword;
  return (
    <div>
      {isLoading && <GlobalLoader />}
      <div className="container rounded custom-shadow mt-4">
        <div className="row p-3 mx-2">
          {/* Profile Header */}
          <div className="col-2">
            <div className="d-flex align-items-center">
              <img
                src={avatarPreview || userProfileImage}
                alt="Profile-Image"
                className="rounded me-3 rounded-circle"
                width="80"
                height="80"
              />
            </div>
          </div>
          <div className="col-9 px-0">
            <div className="fnt14 fw-500" title={pageAccessData?.full_name}>
              {truncateText(pageAccessData?.full_name, 50)}
            </div>
            <div className="d-flex align-items-center mt-2">
              <div className="fnt12 fw-500 me-4">
                <span className="me-2 primary-color">
                  <i class="bi bi-person-circle"></i>
                </span>
                {pageAccessData?.role_name}
              </div>
              <div className="fnt12 fw-500 ">
                <span className="me-2 primary-color">
                  <i class="bi bi-envelope"></i>
                </span>
                {pageAccessData?.email}
              </div>
            </div>
          </div>
        </div>

        <div className="row p-3">
          <div className="col-12 px-0">
            <ul className="nav user-profile-nav">
              <li className="nav-item fnt12">
                <button
                  className={`nav-link ${
                    activeTab === "overview" ? "active" : ""
                  }`}
                  onClick={() => setActiveTab("overview")}
                >
                  Overview
                </button>
              </li>
              <li className="nav-item fnt12">
                <button
                  className={`nav-link ${
                    activeTab === "profile" ? "active" : ""
                  }`}
                  onClick={() => setActiveTab("profile")}
                >
                  Profile
                </button>
              </li>
              <li className="nav-item fnt12">
                    <button
                      className={`nav-link ${
                        activeTab === "pageAccess" ? "active" : ""
                      }`}
                      onClick={() => setActiveTab("pageAccess")}
                    >
                      Page Access
                    </button>
                  </li>
              {/* Hide Page Access and Plant/Tree Access if user is admin */}
              {!isAdmin && (
                <>
                  <li className="nav-item fnt12">
                    <button
                      className={`nav-link ${
                        activeTab === "plantTreeAccess" ? "active" : ""
                      }`}
                      onClick={() => setActiveTab("plantTreeAccess")}
                    >
                      Plant/Tree Access
                    </button>
                  </li>
                </>
              )}
            </ul>
          </div>
        </div>
      </div>

      {/* Profile Details */}
      <div className="container rounded custom-shadow mt-4 px-0">
        {activeTab === "overview" && (
          <div className="mt-3">
            <div className="py-3 px-4 fnt14 fw-600">Overview</div>
            <hr className="mt-0" />
            <div className="fnt12 fw-500 px-4 py-3">
              <div className="row">
                <div className="col-2 text-secondary fw-500">
                  <div className="py-2">Full Name:</div>
                  <div className="py-2">User Name:</div>
                  <div className="py-2">Email:</div>
                  <div className="py-2">Role:</div>
                  <div className="py-2">Designation:</div>
                  <div className="py-2">Status:</div>
                </div>

                <div className="col-10 fw-500">
                  <div className="py-2" title={pageAccessData?.full_name}>
                    {truncateText(pageAccessData?.full_name, 50)}
                  </div>
                  <div className="py-2">{pageAccessData?.username}</div>
                  <div className="py-2">{pageAccessData?.email}</div>
                  <div className="py-2">{pageAccessData?.role_name}</div>
                  <div className="py-2">{pageAccessData?.designation}</div>
                  <div className="py-2">
                    <span className="badge bg-success">Active</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {activeTab === "profile" && (
          <div className="mt-3">
            <div className="py-3 px-4 fnt14 fw-600">Profile Details</div>
            <hr className="mt-0" />
            <form>
              <div className="row mb-3 mt-2 px-5 py-3">
                <div className="col-md-10 ms-1">
                  <label htmlFor="avatarUpload" className="position-relative">
                    <img
                      src={avatarPreview || userProfileImage}
                      alt="Avatar"
                      className="rounded-circle border"
                      width="100"
                      height="100"
                    />
                    <span
                      className="position-absolute top-0 end-0 theme-container border rounded-circle p-1"
                      style={{ cursor: "pointer" }}
                    >
                      <i className="bi bi-pencil"></i>
                    </span>
                  </label>
                  <input
                    type="file"
                    id="avatarUpload"
                    className="d-none"
                    onChange={handleAvatarChange}
                  />
                  {errorMsg && (
                    <small className="text-danger mt-1 ps-3">{errorMsg}</small>
                  )}
                </div>

                <div className="row mb-3 mt-4">
                  <label className="col-4 col-form-label fnt12 fw-500">
                    Full Name*
                  </label>
                  <div className="col-4">
                    <input
                      type="text"
                      className="form-control"
                      value={formData.firstname}
                      placeholder="First Name"
                      onChange={(e) =>
                        setFormData({ ...formData, firstname: e.target.value })
                      }
                    />
                  </div>
                  <div className="col-4">
                    <input
                      type="text"
                      className="form-control"
                      value={formData.lastname}
                      placeholder="Last Name"
                      onChange={(e) =>
                        setFormData({ ...formData, lastname: e.target.value })
                      }
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <label className="col-4 col-form-label fnt12 fw-500">
                    User Name*
                  </label>
                  <div className="col-8">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="User Name"
                      value={formData.username}
                      disabled
                      readOnly
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <label className="col-4 col-form-label fnt12 fw-500">
                    Email*
                  </label>
                  <div className="col-8">
                    <input
                      type="email"
                      className="form-control"
                      placeholder="Email"
                      value={formData.email}
                      disabled
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <label className="col-4 col-form-label fnt12 fw-500">
                    Role
                  </label>
                  <div className="col-8">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Role Name"
                      value={formData.role_name}
                      disabled
                    />
                  </div>
                </div>
              </div>

              <div className="pb-3 px-5 d-flex justify-content-end">
                <button
                  type="button"
                  className="btn btn-secondary py-2 px-3 me-2 fw-500"
                  onClick={() => handleDiscardUserDetails()}
                >
                  Discard
                </button>
                <button
                  type="button"
                  className="btn btn-primary py-2 px-3 fw-500"
                  onClick={() => handleEditUser()}
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        )}
        {activeTab === "pageAccess" && (
          <div className="mt-3">
            <div className="py-3 px-4 fnt14 fw-600">Page Level Access</div>
            <hr className="mt-0" />
            <div className="fnt12 fw-500 px-4 py-3">
              <div className="row">
                <div className="col-12 px-3">Pages</div>
                <div className="fnt12 fw-400 px-2 py-3">
                  <FancyTree
                    data={filteredTree || []}
                    onChange={setSelectedPageAccess}
                    initialChecked={featurePermissions || []}
                    disabled={true}
                    mode="feature"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        {activeTab === "plantTreeAccess" && (
          <div className="mt-3">
            <div className="py-3 px-4 fnt14 fw-600">Plant and Tree Access</div>
            <hr className="mt-0" />
            <div className="fnt12 fw-500 px-4 py-3">
              <div className="row">
                <div className="col-12 px-3">Plant and Tree</div>
                <div className="fnt12 fw-400 px-2 py-3">
                  {/* Plant/Tree Access */}
                  <FancyTree
                    data={filteredPlantTree || []}
                    onChange={setSelectedTreeAccess}
                    initialChecked={initialPlantKeys} // encoded keys
                    disabled={true}
                    mode="entity"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Reset Password */}
      {activeTab === "profile" && (
        <div className="container rounded custom-shadow mt-4 px-0">
          <div className="mt-3">
            <div className="p-3 fnt14 fw-600">Sign-in Method</div>
            <hr className="mt-0" />

            <div className="p-3 d-flex flex-column">
              {isResetting ? (
                <div className="row px-3">
                  {/* Current Password Input */}
                  <div className="col-4 mb-3 position-relative">
                    <label className="fnt14 fw-500 form-label">
                      Current Password
                    </label>
                    <div>
                      <input
                        id="currentPassword"
                        type="text"
                        placeholder="Enter Your Current Password"
                        className="form-control pe-5"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  {/* New Password Input */}
                  <div className="col-4 mb-3 position-relative">
                    <label className="fnt14 fw-500 form-label">
                      New Password
                    </label>
                    <div className="position-relative">
                      <input
                        id="newPassword"
                        type={showNewPassword ? "text" : "password"}
                        placeholder="Enter Your Password"
                        className="form-control pe-5"
                        value={newPassword}
                        onChange={handleNewPasswordChange}
                        required
                      />
                      <img
                        src={showNewPassword ? noHide : hide}
                        className="position-absolute top-50 translate-middle-y end-0 me-3 cr-cursor-pointer"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        style={{
                          cursor: "pointer",
                          width: "20px",
                          height: "20px",
                        }}
                        alt="Toggle password visibility"
                      />
                    </div>
                    {error.newPassword && (
                      <div className="text-danger mt-1">
                        {error.newPassword}
                      </div>
                    )}
                  </div>

                  {/* Confirm Password Input */}
                  <div className="col-4 mb-3 position-relative">
                    <label className="fnt14 fw-500 form-label">
                      Confirm New Password
                    </label>
                    <div className="position-relative">
                      <input
                        id="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Confirm Your Password"
                        className="form-control pe-5"
                        value={confirmPassword}
                        onChange={handleConfirmPasswordChange}
                        required
                      />
                      <img
                        src={showConfirmPassword ? noHide : hide}
                        className="position-absolute top-50 translate-middle-y end-0 me-3 cr-cursor-pointer"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        style={{
                          cursor: "pointer",
                          width: "20px",
                          height: "20px",
                        }}
                        alt="Toggle password visibility"
                      />
                    </div>
                    {error.confirmPassword && (
                      <div className="text-danger mt-1">
                        {error.confirmPassword}
                      </div>
                    )}
                  </div>

                  {/* Buttons */}
                  <div className="d-flex">
                    <button
                      type="submit"
                      className="btn btn-primary me-3 py-2 px-3 fw-500"
                      onClick={handleUpdatePassword}
                      disabled={isButtonDisabled}
                    >
                      Update Password
                    </button>
                    <button
                      className="btn btn-secondary py-2 px-3 fw-500"
                      onClick={() => setIsResetting(false)}
                    >
                      Cancel
                    </button>
                  </div>

                  {message && (
                    <div className="alert alert-info mt-2">{message}</div>
                  )}
                </div>
              ) : (
                <div className="p-3 d-flex justify-content-between align-items-center">
                  <div>
                    <div className="fnt14 fw-500">Password</div>
                    <div>**********</div>
                  </div>
                  <div>
                    <button
                      className="btn btn-primary py-2 px-3 fw-500"
                      onClick={handleResetPassword}
                    >
                      Reset Password
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <SuccessPopup
        type={showSuccessPopup?.type}
        show={showSuccessPopup?.show}
        message={showSuccessPopup?.message}
        onClose={() => {
          setShowSuccessPopup({
            type: "",
            show: false,
            message: "",
          });

          if (shouldRedirectAfterPopup) {
            setShouldRedirectAfterPopup(false);
            localStorage.clear();
            sessionStorage.clear();
            navigate("/login");
          }
        }}
      />
    </div>
  );
};

export default UserProfile;
